import {
  getDefaultVisibleColumnKeys,
  getTableColumnKeys,
  getTableConfig,
} from "#/config/tableColumnsConfig";
import type { Client } from "#/types/client";

/**
 * The column ids a client actually sees for a table, in config order.
 *
 * - No saved restriction for the table: the table's default set.
 * - Saved restriction: the saved keys that still exist in the config, plus the
 *   table's required column. Keys from an older config are ignored, and if
 *   nothing valid is left the default set is used, so a table can never
 *   render empty because of stale settings.
 * Actions and selection checkboxes are never part of this list.
 */
export const getVisibleColumnKeys = (
  tableKey: string,
  client: Client | null | undefined,
): string[] => {
  const config = getTableConfig(tableKey);
  if (!config) return [];
  const known = getTableColumnKeys(tableKey);
  const saved = client?.visibleColumns?.[tableKey];

  if (!Array.isArray(saved) || saved.length === 0) {
    return getDefaultVisibleColumnKeys(tableKey);
  }

  const wanted = new Set(saved.filter((key) => known.includes(key)));
  if (wanted.size === 0) {
    return getDefaultVisibleColumnKeys(tableKey);
  }
  wanted.add(config.requiredColumn);
  return known.filter((key) => wanted.has(key));
};

/** Whether one column is shown for the client. "actions" is always shown. */
export const isColumnVisible = (
  tableKey: string,
  columnKey: string,
  client: Client | null | undefined,
): boolean => {
  if (columnKey === "actions") return true;
  return getVisibleColumnKeys(tableKey, client).includes(columnKey);
};

/** @deprecated use getVisibleColumnKeys */
export const getVisibleColumns = getVisibleColumnKeys;

export const hasVisibleColumns = (tableKey: string, client: Client | null | undefined): boolean =>
  getVisibleColumnKeys(tableKey, client).length > 0;
